import { useMemo } from "react"
import type { AddressInput } from "@/modules/care-provider/simple-search/services/__generated__/useCareProviderQuickListQuery.graphql"
import type { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import type { Country } from "@/types/__generated__/union-types"

type UseAppointmentOrRegisteredPatientAddressParameters = {
  patientId: string
  countryCode: Country | undefined
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
}

export const useAppointmentOrRegisteredPatientAddress = ({
  patientId,
  countryCode,
  episodeOfCareProgressNoteData,
}: UseAppointmentOrRegisteredPatientAddressParameters): AddressInput | null => {
  const appointmentId = useAppointmentParam()
  const [patientData] = usePatientByIdQuery(patientId, countryCode ?? "CH", appointmentId)

  const { patientAddress: appointmentPatientAddress } = episodeOfCareProgressNoteData.appointment
  const registeredPatientAddress = patientData?.patientById?.address

  return useMemo(
    () =>
      appointmentPatientAddress ||
      (registeredPatientAddress
        ? {
            countryCode: registeredPatientAddress.countryCode,
            city: registeredPatientAddress.city,
            houseNumberStreet: registeredPatientAddress.houseNumberStreet,
            postalCode: registeredPatientAddress.postalCode,
          }
        : null),
    [appointmentPatientAddress, registeredPatientAddress],
  )
}
